import {
    Button,
    DialogActions,
    DialogContent,
    Divider,
    FormControl,
    FormGroup,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import DialogHESP from "../DialogHESP/DialogHESP";
import DialogFooterWithLoading from "../Loading/DialogFooterWithLoading";
import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";

import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import { GET_DATACENTER } from "../../views/App/TLADatacenter";
import { GetDatacenter_datacenter_tla_deviceContexts } from "../../views/App/__generated__/GetDatacenter";
import {
    UpdateDeviceContextMutation,
    UpdateDeviceContextMutationVariables,
} from "./__generated__/UpdateDeviceContextMutation";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import { v4 } from "uuid";
import { Add } from "@mui/icons-material";
import TLALabelsManager from "./TLALabelsManager";
import JSONInput, { isValidJSON } from "../JSONInput/JSONInput";
import SwitchWithTitle from "../SwitchWithTitle/SwitchWithTitle";

interface Props {
    open: boolean;
    datacenterId: string;
    deviceContext: GetDatacenter_datacenter_tla_deviceContexts;
    onClose: () => void;
}

export const UPDATE_TLA_DEVICE_CONTEXT = gql`
    mutation UpdateDeviceContextMutation($input: UpdateDeviceContextInput!) {
        updateDeviceContext(input: $input) {
            id
        }
    }
`;

export default function TLAUpdateDeviceContextDialog({ onClose, open, deviceContext, datacenterId }: Props) {
    const [updateDeviceContext, { loading }] = useMutation<
        UpdateDeviceContextMutation,
        UpdateDeviceContextMutationVariables
    >(UPDATE_TLA_DEVICE_CONTEXT);

    const [capacity, setCapacity] = useState<number>(deviceContext.capacity);
    const [name, setName] = useState<string>(deviceContext.name);
    const [description, setDescription] = useState<string>(deviceContext.description);
    const [privateIp, setPrivateIp] = useState<string>(deviceContext.privateIp ?? "");
    const [joinToken, setJoinToken] = useState<string>(deviceContext.joinToken ?? "");
    const [isCluster, setIsCluster] = useState<boolean>(deviceContext.isCluster === true);
    const [labels, setLabels] = useState<Record<string, string>>(arrayToRecord(deviceContext.labels));
    const [override, setOverride] = useState<string>(deviceContext.override ?? "");
    const [labelsOk, setLabelsOk] = useState<boolean>(true);

    const [firstSubmitDone, setFirstSubmitDone] = useState<boolean>(false);

    useEffect(() => {
        setCapacity(deviceContext.capacity);
        setName(deviceContext.name);
        setDescription(deviceContext.description);
        setPrivateIp(deviceContext.privateIp ?? "");
        setJoinToken(deviceContext.joinToken ?? "");
        setIsCluster(deviceContext.isCluster === true);
        setLabels(arrayToRecord(deviceContext.labels));
        setOverride(deviceContext.override ?? "");
        setLabelsOk(true);
    }, [open, deviceContext]);

    function notEmpty(val: string): boolean {
        if (val.trim().length === 0) {
            return false;
        }
        return true;
    }

    const regex = /([A-z0-9/+-.,])*=([A-z0-9/+-.,])*/;

    function arrayToRecord(lbls: string[]): Record<string, string> {
        const record: Record<string, string> = {};

        lbls.forEach((label) => (record[v4()] = label));
        return record;
    }

    async function onConfirm() {
        setFirstSubmitDone(true);
        if (notEmpty(capacity.toString()) && labelsOk) {
            try {
                await updateDeviceContext({
                    variables: {
                        input: {
                            capacity: parseInt(capacity.toString(), 10),
                            id: deviceContext.serial,
                            description,
                            labels: Object.values(labels),
                            name,
                            isCluster,
                            joinToken: joinToken.trim().length > 0 ? joinToken : undefined,
                            override: override.trim().length > 0 ? override : undefined,
                            privateIp: privateIp.trim().length > 0 ? privateIp : undefined,
                        },
                    },
                    refetchQueries: [
                        {
                            query: GET_DATACENTER,
                            variables: {
                                id: datacenterId,
                            },
                        },
                    ],
                });
                onClose();
            } catch (e) {
                toast.error("Error while removing device context", TOAST_SETTINGS);
            }
        }
    }

    function validLabels(lbls: string[]) {
        let isValid = true;
        lbls.forEach((label) => {
            if (!regex.test(label)) {
                isValid = false;
            }
        });
        return isValid;
    }

    function onRemoveLabel(idx: string) {
        const newList: Record<string, string> = {};
        for (let key in labels) {
            if (key !== idx) {
                newList[key] = labels[key];
            }
        }
        setLabels(newList);
        setLabelsOk(validLabels(Object.values(newList)));
    }

    function onChangeLabel(idx: string, value: string) {
        const copyLabels = labels;
        copyLabels[idx] = value;
        setLabels(copyLabels);
        setLabelsOk(validLabels(Object.values(copyLabels)));
    }

    return (
        <DialogHESP
            title={"Update device context"}
            open={open}
            onClose={onClose}
            content={
                <>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Name" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={name}
                                        placeholder="Name"
                                        onChange={(e: any) => setName(e.target.value)}
                                    />
                                    {!notEmpty(name) && firstSubmitDone && (
                                        <Typography variant="subtitle2" color="error">
                                            Please enter a valid name
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Description" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={description}
                                        placeholder="Description"
                                        onChange={(e: any) => setDescription(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormGroup>
                                    <HESPFormLabel label="Capacity" />
                                    <TextField
                                        type="number"
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={capacity}
                                        placeholder="Capacity"
                                        onChange={(e: any) => setCapacity(e.target.value)}
                                    />
                                    {!notEmpty(capacity.toString()) && firstSubmitDone && (
                                        <Typography variant="subtitle2" color="error">
                                            Please enter a valid capacity
                                        </Typography>
                                    )}
                                </FormGroup>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Private IP" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={privateIp}
                                        placeholder="Private IP"
                                        onChange={(e: any) => setPrivateIp(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Join token" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={joinToken}
                                        placeholder="Join token"
                                        onChange={(e: any) => setJoinToken(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Is cluster" />
                                    <SwitchWithTitle
                                        checked={isCluster}
                                        onChange={() => setIsCluster(!isCluster)}
                                        title="Use as cluster"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Override" />
                                    <JSONInput
                                        input={override}
                                        onChange={(input: string) => setOverride(input)}
                                        placeholder="Override object"
                                    />
                                    {override !== "" && !isValidJSON(override) && (
                                        <Typography variant="subtitle2" color="error">
                                            Invalid JSON object
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                Labels
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <TLALabelsManager
                                        labels={labels}
                                        onChangeLabel={(idx: string, val: string) => onChangeLabel(idx, val)}
                                        onRemoveLabel={(idx: string) => onRemoveLabel(idx)}
                                        regex={regex}
                                    />
                                </FormControl>
                                <FormControl fullWidth style={{ display: "flex", justifyContent: "center" }}>
                                    <Button
                                        color="secondary"
                                        startIcon={<Add />}
                                        onClick={() => {
                                            const copyLabels = labels;
                                            copyLabels[v4()] = "";
                                            setLabels({ ...copyLabels });
                                            setLabelsOk(validLabels(Object.values(copyLabels)));
                                        }}
                                    >
                                        Add label
                                    </Button>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <DialogFooterWithLoading
                            isLoading={loading}
                            onCancelClick={onClose}
                            onActionClick={onConfirm}
                            defaultText={"Update"}
                            loadingText={"Updating..."}
                        />
                    </DialogActions>
                </>
            }
        />
    );
}
